import type { ArticleShort } from "lib/types/article";

/**
 * Filter articles for "Popular" tab
 * Popular = ALL articles (no filtering, editorial curation only)
 *
 * @param articles - All articles from CMS
 * @returns All articles unfiltered
 */
export const filterPopularArticles = (
  articles: ArticleShort[]
): ArticleShort[] => {
  return articles;
};

/**
 * Filter articles for "Free to Read" tab
 * Free to Read = Not Pro AND Not paywalled
 *
 * Criteria:
 * - isProContent === false (not Pro-only content)
 * - isNonFree === false (not behind paywall)
 *
 * @param articles - All articles from CMS
 * @returns Filtered articles that are free to read
 */
export const filterFreeToReadArticles = (
  articles: ArticleShort[]
): ArticleShort[] => {
  return articles.filter(
    (article) => article.isProContent === false && article.isNonFree === false
  );
};

/**
 * Filter articles for "Collaborations" tab
 * Collaborations = Sponsored AND Free
 *
 * Criteria:
 * - isSponsored === true (has sponsorship)
 * - isNonFree === false (not behind paywall)
 *
 * Note: This includes all sponsorship types (Sponsored by, In partnership with)
 *
 * @param articles - All articles from CMS
 * @returns Filtered articles that are sponsored and free
 */
export const filterCollaborationArticles = (
  articles: ArticleShort[]
): ArticleShort[] => {
  return articles.filter(
    (article) => article.isSponsored === true && article.isNonFree === false
  );
};

/**
 * Batch filter articles into all three categories
 * Convenience function for use in getStaticProps
 *
 * @param articles - All articles from CMS
 * @returns Object with three filtered article arrays
 *
 * @example
 * const { popularArticles, freeArticles, collaborationArticles } =
 *   filterArticlesForFeed(allArticles);
 */
export const filterArticlesForFeed = (articles: ArticleShort[]) => {
  return {
    popularArticles: filterPopularArticles(articles),
    freeToReadArticles: filterFreeToReadArticles(articles),
    collaborationArticles: filterCollaborationArticles(articles),
  };
};

/**
 * Exclude sponsored/paid/branded article types from the homepage hero's
 * "Latest Articles" list, based on the article's main `category`.
 *
 * `excludedCategories` is editor-managed via DatoCMS
 * (`homepage.latestArticlesCategoriesFilter`); when omitted, nothing is
 * excluded.
 */
export const isExcludedFromLatestArticles = (
  article: ArticleShort,
  excludedCategories: string[] = []
): boolean => {
  const categoryName = article.category?.name;

  return !!categoryName && excludedCategories.includes(categoryName);
};
